import { useClientStore } from '@/stores/client'
import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { validateLanguage } from '@/helpers/shared'
import { loadClientStylesheet } from '@/helpers/client-theme'

export const clientGuard = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
): Promise<void> => {
  const clientStore = useClientStore()

  // TODO: only allow standalone in test and development environment
  const ALLOWED_HOSTNAMES = [
    'localhost',
    'core-widget.localhost',
    'widget.core-value.lo',
    'app.core-value.ch',
    'widget.exohuzan.myhostpoint.ch',
    'widget.core-value.dev',
  ]

  if (!clientStore.getClientQuery) {
    /* first load of the widget by client (or reload by errorView/Dialog) */
    if (to?.query?.key) {
      /* to.query?.reload -> reload of widget triggered by ErrorView/Dialog */
      const referrer: string = to.query?.reload
        ? localStorage.getItem('core-widget-referrer') || ''
        : new URL(document.referrer).origin

      const clientQuery = {
        uuid: to?.query?.key as string /* apiKey */,
        origin: referrer,
        language: validateLanguage(to?.query?.lang as string),
        requestType: (to?.query?.type as string) || 'project_units',
        cssPath: (to?.query?.cssPath as string) || '/style.css',
        nonce: (to?.query?.nonce as string) || null,
        message: (to?.query?.message as string) || null,
      }
      clientStore.setClientQuery(clientQuery)

      if (clientQuery.requestType === 'office_architect') {
        // Todo: find a better solution to redirect other widget requestTypes
        clientStore.setClientReferrer(to.fullPath.replace('/project/general', '/office/architect'), referrer)
        /* INFO: clientGuard is triggered on redirect page again
         * redirect with next() is ok because initClient is called there */
        next({ name: 'office-architect' })
        return
      }
      clientStore.setClientReferrer(to.fullPath, referrer)
    } else if (ALLOWED_HOSTNAMES.indexOf(document.location.hostname) > -1) {
      /* INFO: standalone widget is only allowed for development */
      const widgetType = (to?.meta?.widget as string) || null
      clientStore.setClientQuery(null, widgetType)
    } else {
      /* INFO: forbidden routing without key */
      next({ name: 'error', params: { key: 'errors.client.bad_request' } })
      return
    }
  }

  if (!clientStore.getUser && clientStore.getClientQuery) {
    const error = await clientStore.initClient(clientStore.getClientQuery)
    if (!error) {
      loadClientStylesheet(clientStore.getClientQuery.origin, clientStore.getClientQuery.cssPath)
    }
    if (!clientStore.authenticated && error) {
      const errorKey = 'errors.api.' + error?.message
      next({ name: 'error', params: { key: errorKey } })
    }
  }
  next()
}
